import * as React from "react";
import {Body, Section} from "@athena-portal/components";
import {IApplicationGuard, lazyInject, TypeDefs} from "@athena-portal/core";
import {
    CommonHelpers,
    ICustomerPortalService,
    INotification,
    INotificationService,
    IFalconConvenienceApiService,
    SessionStorageItemEnum,
    ProductsEnum,
    Policy,
    Types, resourcesHelper
} from "@falcon/services";
import {MultiTabBroadcastEvents, StoragesKeys} from "./components/enums/enums"
import {VersionDisplay} from "./VersionDisplay";
import {Navigate} from "react-router-dom";
interface IState {
    doRedirect: boolean,
    doRedirectToEventWarningPage: boolean,
    notificationMessage: string,
    multiTabCheckTimer: any,
}

export class Layout extends React.Component<{}, IState> {
    private readonly MULTI_TAB_CHECK_INTERVAL = 2000;
    private readonly MULTI_TAB_PARTIAL_LINKS_TO_IGNORE = [
        "customerEmailNotMatchPage",
        "authVoid"
    ]

    @lazyInject(Types.NotificationService)
    private notificationService: INotificationService;

    @lazyInject(TypeDefs.ApplicationGuard)
    private readonly applicationGuard: IApplicationGuard;

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    @lazyInject(Types.FalconConvenienceApiService)
    private falconConvenienceApiService: IFalconConvenienceApiService

    private readonly multiTabCommunicationChannel = new BroadcastChannel("multiTabCheck");

    private multiTabLockStarted: boolean =  false;

    constructor(props: {}) {
        super(props);
        const multiTabCheckTimer = setInterval(() => this.enforceSingleActiveTab(), this.MULTI_TAB_CHECK_INTERVAL);
        this.state = {
            doRedirect: false,
            doRedirectToEventWarningPage: false,
            notificationMessage: "",
            multiTabCheckTimer,
        };
        this.enforceSingleActiveTab = this.enforceSingleActiveTab.bind(this);
    }

    public componentDidMount() {
        this.removeAggregatorLoadingScreen();

        this.notificationService.getFeed().subscribe((note: INotification) => {
            if (note && this.state.notificationMessage.indexOf(note.message) === -1) {
                this.setState({
                    doRedirect: true,
                    notificationMessage: `${this.state.notificationMessage}\n${note.message}`
                })
            }
        });

        if (window.location.pathname.includes("/dashboard")) {
            this.setRedirectToEventWarningPage();
        }
        this.multiTabCommunicationChannel.onmessage = this.processMultiTabBroadcastChannelMessages()
    }

    public render() {
        return (
            <React.Fragment>
                {(this.doRedirectToEventWarningPage()) && (<Navigate to={`/EventWarningPage`}/>)}
                <div>
                    <div>
                        <div className="container-fluid">
                            <div className="container pt-3 pb-3">
                                <Body/>
                            </div>
                        </div>
                    </div>
                </div>
                <footer className="footer-position">
                    <Section id="footer"/>
                </footer>
                <VersionDisplay/>
                {this.doRedirect() && (<Navigate to={`/error?e=${this.state.notificationMessage}`}/>)}
            </React.Fragment>
        );
    }

    // Method that runs infinitely with time delay to check if there are multiple tabs
    // If multiple tab is detected redirect will happen for multiTab page(redirecting from multiTab to multiTab will be ignored)
    // MULTI_TAB_PARTIAL_LINKS_TO_IGNORE - will have list of links that will ignore multiTab logic
    private enforceSingleActiveTab() {
        if (!this.applicationGuard.isAuthenticated() || this.MULTI_TAB_PARTIAL_LINKS_TO_IGNORE.some(ignoredLink => window.location.pathname.includes(ignoredLink))) {
            return;
        }
        // This is required to reset process after first run after page reload or recreation - to avoid log in/log out issues.
        if (!this.multiTabLockStarted) {
            localStorage.removeItem(this.getLockUserTabKey());
            this.multiTabLockStarted = true;
        }
        // uniqueTabInstanceId is required to detect when tab was duplicated with tabId - it changes every time multiTab check is called
        const uniqueTabInstanceId = crypto.randomUUID();
        sessionStorage.setItem(StoragesKeys.UNIQUE_TAB_INSTANCE_ID, uniqueTabInstanceId);
        this.multiTabCommunicationChannel.postMessage({
            type: MultiTabBroadcastEvents.INSTANCE_CHECK_EVENT,
            uniqueTabInstanceId,
        });
        let tabId = sessionStorage.getItem(StoragesKeys.TAB_ID);
        if (!tabId) {
            tabId = crypto.randomUUID();
            sessionStorage.setItem(StoragesKeys.TAB_ID, tabId);
            localStorage.setItem(this.getLockUserTabKey(), tabId);
            return;
        }

        let lockUserTabKey = localStorage.getItem(this.getLockUserTabKey())
        if (lockUserTabKey && lockUserTabKey != tabId && !window.location.pathname.includes("multiTab")) {
            sessionStorage.setItem(StoragesKeys.BACK_REDIRECT, window.location.pathname);
            window.location.assign("/multiTab");
        }

        localStorage.setItem(this.getLockUserTabKey(), tabId);
    }

    private getLockUserTabKey() {
        return "partyLockTabId_" + resourcesHelper.THEME + "_" + this.applicationGuard.getUser()?.name;
    }


    private processMultiTabBroadcastChannelMessages() {
        return (event: any) => {
            if (event.data.type === MultiTabBroadcastEvents.REDIRECT_ALL_OTHER_TABS_EVENT && sessionStorage.getItem(StoragesKeys.TAB_ID) !== event.data.tabId) {
                if (resourcesHelper.THEME === "Britannia") {
                    window.location.assign("https://www.britanniainsurance.co.uk/");
                } else {
                    window.location.assign("https://www.allianz.co.uk")
                }
            } else if (event.data.type === MultiTabBroadcastEvents.INSTANCE_CHECK_EVENT) {
                if (event.data.uniqueTabInstanceId !== sessionStorage.getItem(StoragesKeys.UNIQUE_TAB_INSTANCE_ID)) {
                    sessionStorage.setItem(StoragesKeys.TAB_ID, crypto.randomUUID());
                }
            } else if (event.data.type === MultiTabBroadcastEvents.RETURN_OTHER_TABS_TO_LAST_PAGE) {
                if (sessionStorage.getItem(StoragesKeys.TAB_ID) !== event.data.tabId) {
                    sessionStorage.removeItem(StoragesKeys.TAB_ID);
                    localStorage.removeItem(this.getLockUserTabKey());
                    const backRedirectPath = sessionStorage.getItem(StoragesKeys.BACK_REDIRECT);
                    if (backRedirectPath) {
                        window.location.assign(backRedirectPath);
                        sessionStorage.removeItem(StoragesKeys.BACK_REDIRECT)
                    } else {
                        history.back();
                    }
                }
            }
        };
    }

    private async getPolicies() {
        const observable = this.customerPortalService.getPolicies();
        return await observable.toPromise();
    }


    private async getSettings() {
        const observable = this.falconConvenienceApiService.getSettings();
        return await observable.toPromise();
    }

    private removeAggregatorLoadingScreen = () => {
        const loadingScreenContainer = document.getElementById("loading-screen-container");
        if (loadingScreenContainer) {
            loadingScreenContainer.classList.add("d-none");
        }
    }

    private doRedirect = (): boolean => {
        if (this.state.doRedirect) {
            this.setState({doRedirect: false});
            return true;
        }


        return false;
    }

    private doRedirectToEventWarningPage = (): boolean => {
        if (this.state.doRedirectToEventWarningPage) {
            this.setState({doRedirectToEventWarningPage: false});
            return true;
        }

        return false;
    }

    private async setRedirectToEventWarningPage() {
        const isEventWarningPageShown = CommonHelpers.getSessionStorage(SessionStorageItemEnum.SESSION_STORAGE_EVENT_WARNING_PAGE_SHOWN);
        if (isEventWarningPageShown) {
            return;
        }

        const settings: any = await this.getSettings();

        const enabledForMotor: boolean = settings?.eventWarningPage?.motor?.enabled === true;
        const enabledForHome: boolean = settings?.eventWarningPage?.home?.enabled === true;
        const policies: Policy[] = await this.getPolicies();
        const showEventWarningPage: boolean = policies.some(p =>
            (enabledForMotor && p.products.includes(ProductsEnum.WOLF_MOTOR)) ||
            (enabledForHome && p.products.includes(ProductsEnum.HOME))
        );
        if (showEventWarningPage) {
            this.setState({doRedirectToEventWarningPage: true});
            // Remember that we will show the event warning now, so it is not shown again in the same session.
            CommonHelpers.setSessionStorage(SessionStorageItemEnum.SESSION_STORAGE_EVENT_WARNING_PAGE_SHOWN, true);
        }
    }
}
